import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, Folder, FolderOpen, MessageSquarePlus, XCircle } from 'lucide-react';
import type { BinderFolderRow } from '@/api/client';
import { cn } from '@/lib/cn';

/**
 * Per-folder completeness signal returned by GET /binders/{id}. Backs the
 * eISF folder-tree story ACs:
 *   AC4 — folder-level completeness indicator
 *   AC5/6 — red XCircle when a required doc is missing, green CheckCircle2
 *           when all required docs are filed
 *   AC3 — total_doc_count powers the "N docs" badge on each leaf folder
 *
 * The BE only marks LEAF folders. We roll UP to section + zone via
 * `rollUpCompleteness()` below so the parent rows also show an indicator
 * (any leaf incomplete → parent incomplete).
 */
interface FolderCompleteness {
  folder_id: string;
  required_count: number;
  present_count: number;
  /** Present since Sprint-20 for AC3 doc-count badge. Undefined on legacy responses. */
  total_doc_count?: number;
  status: 'complete' | 'incomplete' | 'optional_only';
}

type RollupStatus = 'complete' | 'incomplete' | 'optional_only';

/**
 * Roll a set of children into a single status. Rule:
 *   - any child 'incomplete'      → parent 'incomplete'
 *   - else any child 'complete'   → parent 'complete'
 *   - else                        → 'optional_only' (no Core artefacts anywhere below)
 * Ignores undefined entries (folders without completeness data).
 */
function rollUpCompleteness(
  entries: Array<FolderCompleteness | undefined>,
): { status: RollupStatus; total_required: number; total_present: number; total_docs: number } {
  let hasIncomplete = false;
  let hasComplete = false;
  let total_required = 0;
  let total_present = 0;
  let total_docs = 0;
  for (const e of entries) {
    if (!e) continue;
    total_required += e.required_count;
    total_present += e.present_count;
    total_docs += e.total_doc_count ?? 0;
    if (e.status === 'incomplete') hasIncomplete = true;
    else if (e.status === 'complete') hasComplete = true;
  }
  const status: RollupStatus = hasIncomplete
    ? 'incomplete'
    : hasComplete
      ? 'complete'
      : 'optional_only';
  return { status, total_required, total_present, total_docs };
}

interface CompletenessDotProps {
  status: RollupStatus;
  presentCount: number;
  requiredCount: number;
  size?: 'sm' | 'xs';
}

function CompletenessIcon({ status, presentCount, requiredCount, size = 'sm' }: CompletenessDotProps) {
  if (status === 'optional_only') return null;
  const isComplete = status === 'complete';
  const iconClass = size === 'xs' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const label = isComplete ? 'Complete' : 'Missing required doc';
  const tip = isComplete
    ? 'All required documents filed'
    : `Required document missing (${presentCount}/${requiredCount})`;
  return isComplete ? (
    <CheckCircle2
      className={cn('shrink-0 text-emerald-600', iconClass)}
      aria-label={label}
    >
      <title>{tip}</title>
    </CheckCircle2>
  ) : (
    <XCircle
      className={cn('shrink-0 text-rose-600', iconClass)}
      aria-label={label}
    >
      <title>{tip}</title>
    </XCircle>
  );
}

export interface BinderFolderTreeProps {
  folders: BinderFolderRow[];
  selectedFolderId: string | null;
  onSelect: (folderId: string) => void;
  completeness?: Record<string, FolderCompleteness>;
  /**
   * Phase 4 base FE — Jawahar AC 4.1 "+Query" action on the folder tree.
   * When supplied, each leaf folder row shows an inline button that
   * opens a Raise Query dialog with the folder pre-filled. Omit to hide
   * the button entirely (viewer roles that can't raise queries).
   */
  onRaiseQuery?: (folder: BinderFolderRow) => void;
  filter?: string;
}

interface SectionGroup {
  name: string;
  folders: BinderFolderRow[];
}

interface ZoneGroup {
  name: string;
  sections: SectionGroup[];
}

function groupFolders(folders: BinderFolderRow[]): ZoneGroup[] {
  const zoneMap = new Map<string, Map<string, BinderFolderRow[]>>();
  const sorted = [...folders].sort((a, b) => a.sort_order - b.sort_order);

  for (const f of sorted) {
    let sections = zoneMap.get(f.zone_name);
    if (!sections) {
      sections = new Map<string, BinderFolderRow[]>();
      zoneMap.set(f.zone_name, sections);
    }
    let bucket = sections.get(f.section_name);
    if (!bucket) {
      bucket = [];
      sections.set(f.section_name, bucket);
    }
    bucket.push(f);
  }

  return Array.from(zoneMap.entries()).map(([zoneName, sectionMap]) => ({
    name: zoneName,
    sections: Array.from(sectionMap.entries()).map(([sectionName, rows]) => ({
      name: sectionName,
      folders: rows,
    })),
  }));
}

function folderDisplayName(f: BinderFolderRow, idx?: number): string {
  return (
    f.display_name_override ||
    f.subartifact_label ||
    f.artifact_type ||
    (idx !== undefined ? `Document ${idx + 1}` : 'Unnamed')
  );
}

type LeafNode = { kind: 'leaf'; id: string; folder: BinderFolderRow; idx: number };
interface ContainerNode {
  kind: 'container';
  id: string;
  label: string;
  depth: number;
  sortOrder: number;
  count: number;
  children: TreeNode[];
  leaves: BinderFolderRow[];
}
type TreeNode = LeafNode | ContainerNode;

function isLeafLevel(f: BinderFolderRow): boolean {
  return f.folder_level === 'artifact' || f.folder_level === 'subartifact';
}

function minSortOrder(rows: BinderFolderRow[]): number {
  return rows.reduce((min, f) => (f.sort_order < min ? f.sort_order : min), Number.POSITIVE_INFINITY);
}

export function BinderFolderTree({
  folders,
  selectedFolderId,
  onSelect,
  completeness,
  onRaiseQuery,
  filter,
}: BinderFolderTreeProps) {
  const query = (filter ?? '').trim().toLowerCase();

  const byFolderId = useMemo(() => {
    const m = new Map<string, BinderFolderRow>();
    for (const f of folders) m.set(f.folder_id, f);
    return m;
  }, [folders]);

  const visibleFolders = useMemo(() => {
    if (!query) return folders;
    const matches = folders.filter(
      (f) =>
        folderDisplayName(f).toLowerCase().includes(query) ||
        (f.display_name ?? '').toLowerCase().includes(query) ||
        (f.zone_name ?? '').toLowerCase().includes(query) ||
        (f.section_name ?? '').toLowerCase().includes(query),
    );
    const keep = new Set<string>();
    for (const f of matches) {
      keep.add(f.folder_id);
      let cur: BinderFolderRow | undefined = f;
      const seen = new Set<string>([f.folder_id]);
      while (cur && cur.parent_folder_id) {
        const p = byFolderId.get(cur.parent_folder_id);
        if (!p || seen.has(p.folder_id)) break;
        keep.add(p.folder_id);
        seen.add(p.folder_id);
        cur = p;
      }
    }
    return folders.filter((f) => keep.has(f.folder_id));
  }, [folders, query, byFolderId]);

  const childrenByParent = useMemo(() => {
    const m = new Map<string, BinderFolderRow[]>();
    for (const f of visibleFolders) {
      if (!f.parent_folder_id) continue;
      const list = m.get(f.parent_folder_id) ?? [];
      list.push(f);
      m.set(f.parent_folder_id, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.sort_order - b.sort_order);
    return m;
  }, [visibleFolders]);

  const topNodes = useMemo(() => {
    const isParent = (id: string) => (childrenByParent.get(id)?.length ?? 0) > 0;

    function buildReal(f: BinderFolderRow, depth: number, idx: number): TreeNode {
      const kids = childrenByParent.get(f.folder_id) ?? [];
      if (kids.length === 0) return { kind: 'leaf', id: f.folder_id, folder: f, idx };
      const children = kids.map((k, i) => buildReal(k, depth + 1, i));
      const leaves = children.flatMap((c) => (c.kind === 'leaf' ? [c.folder] : c.leaves));
      return {
        kind: 'container',
        id: f.folder_id,
        label: f.display_name ?? folderDisplayName(f),
        depth,
        sortOrder: f.sort_order,
        count: children.length,
        children,
        leaves,
      };
    }

    const flatArtifacts = visibleFolders.filter(
      (f) => !f.parent_folder_id && !isParent(f.folder_id) && isLeafLevel(f),
    );
    const realRoots = visibleFolders
      .filter((f) => !f.parent_folder_id && isParent(f.folder_id))
      .map((f) => buildReal(f, 0, 0))
      .filter((n): n is ContainerNode => n.kind === 'container');

    const syntheticZones: ContainerNode[] = groupFolders(flatArtifacts).map((zone) => {
      const sectionNodes: ContainerNode[] = zone.sections.map((section) => {
        const leafNodes: LeafNode[] = section.folders.map((f, i) => ({
          kind: 'leaf',
          id: f.folder_id,
          folder: f,
          idx: i,
        }));
        return {
          kind: 'container',
          id: `sec:${zone.name}__${section.name}`,
          label: section.name,
          depth: 1,
          sortOrder: minSortOrder(section.folders),
          count: section.folders.length,
          children: leafNodes,
          leaves: section.folders,
        };
      });
      const allLeaves = zone.sections.flatMap((s) => s.folders);
      return {
        kind: 'container',
        id: `zone:${zone.name}`,
        label: zone.name,
        depth: 0,
        sortOrder: minSortOrder(allLeaves),
        count: allLeaves.length,
        children: sectionNodes,
        leaves: allLeaves,
      };
    });

    return [...realRoots, ...syntheticZones].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label),
    );
  }, [visibleFolders, childrenByParent]);

  const initialOpen = useMemo(() => {
    const set = new Set<string>();
    if (!selectedFolderId) return set;
    const hit = byFolderId.get(selectedFolderId);
    if (!hit) return set;
    if (hit.parent_folder_id) {
      let cur: BinderFolderRow | undefined = hit;
      const seen = new Set<string>([hit.folder_id]);
      while (cur && cur.parent_folder_id) {
        const p = byFolderId.get(cur.parent_folder_id);
        if (!p || seen.has(p.folder_id)) break;
        set.add(p.folder_id);
        seen.add(p.folder_id);
        cur = p;
      }
    } else {
      set.add(`zone:${hit.zone_name}`);
      set.add(`sec:${hit.zone_name}__${hit.section_name}`);
    }
    return set;
  }, [selectedFolderId, byFolderId]);

  const [openNodes, setOpenNodes] = useState<Set<string>>(() => new Set(initialOpen));

  useEffect(() => {
    if (initialOpen.size === 0) return;
    setOpenNodes((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of initialOpen) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [initialOpen]);

  function toggleNode(id: string) {
    setOpenNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const isOpen = (id: string) => query !== '' || openNodes.has(id);

  function renderLeaf(node: LeafNode) {
    const f = node.folder;
    const selected = f.folder_id === selectedFolderId;
    const label = folderDisplayName(f, node.idx);
    const c = completeness?.[f.folder_id];
    const docCount = c?.total_doc_count ?? 0;
    return (
      <li key={f.folder_id}>
        <button
          type="button"
          onClick={() => onSelect(f.folder_id)}
          aria-current={selected ? 'true' : undefined}
          title={label}
          className={cn(
            'group flex w-full items-center gap-1.5 rounded-sm px-1.5 py-[4px] text-left text-[12px] leading-tight transition-colors',
            selected
              ? 'bg-navy-50 font-medium text-navy'
              : 'text-ink-secondary hover:bg-surface-muted',
          )}
        >
          <Folder
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              selected ? 'text-navy' : 'text-slate-400 group-hover:text-ink-secondary',
            )}
          />
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {docCount > 0 && (
            <span
              className="shrink-0 rounded-full bg-surface-muted px-1.5 py-[1px] text-[10px] font-medium text-ink-secondary"
              title={`${docCount} document${docCount === 1 ? '' : 's'} in this folder`}
            >
              {docCount}
            </span>
          )}
          {c && c.status !== 'optional_only' && (
            <CompletenessIcon
              status={c.status}
              presentCount={c.present_count}
              requiredCount={c.required_count}
              size="xs"
            />
          )}
          {onRaiseQuery && (
            <span
              role="button"
              tabIndex={0}
              aria-label={`Raise a query on ${label}`}
              title="Raise a query on this section"
              onClick={(e) => {
                e.stopPropagation();
                onRaiseQuery(f);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onRaiseQuery(f);
                }
              }}
              className="ml-1 hidden shrink-0 rounded p-0.5 text-accent-700 hover:bg-accent-50 group-hover:inline-flex"
            >
              <MessageSquarePlus className="h-3 w-3" />
            </span>
          )}
        </button>
      </li>
    );
  }

  function renderContainer(node: ContainerNode) {
    const open = isOpen(node.id);
    const rollup = rollUpCompleteness(node.leaves.map((f) => completeness?.[f.folder_id]));
    const isZone = node.depth === 0;
    return (
      <li key={node.id}>
        <button
          type="button"
          onClick={() => toggleNode(node.id)}
          aria-expanded={open}
          title={node.label}
          className={cn(
            'flex w-full items-center gap-1.5 rounded-sm px-1.5 text-left hover:bg-surface-muted transition-colors',
            isZone ? 'py-[5px]' : 'py-[4px]',
          )}
        >
          <ChevronDown
            className={cn(
              'shrink-0 text-ink-muted transition-transform duration-150',
              isZone ? 'h-3.5 w-3.5' : 'h-3 w-3',
              !open && '-rotate-90',
            )}
          />
          {open ? (
            <FolderOpen className={cn('shrink-0', isZone ? 'h-4 w-4 text-amber-500' : 'h-3.5 w-3.5 text-sky-500')} />
          ) : (
            <Folder className={cn('shrink-0', isZone ? 'h-4 w-4 text-amber-500' : 'h-3.5 w-3.5 text-sky-500')} />
          )}
          <span
            className={cn(
              'truncate',
              isZone ? 'text-[12.5px] font-semibold text-ink-primary' : 'text-[12px] font-medium text-ink-secondary',
            )}
          >
            {node.label}
          </span>
          {rollup.total_docs > 0 && (
            <span
              className="ml-auto shrink-0 rounded-full bg-surface-muted px-1.5 py-[1px] text-[10px] font-medium text-ink-secondary"
              title={`${rollup.total_docs} document${rollup.total_docs === 1 ? '' : 's'} in this ${isZone ? 'category' : 'section'}`}
            >
              {rollup.total_docs}
            </span>
          )}
          <span
            className={cn(
              'shrink-0 font-normal text-ink-muted',
              isZone ? 'text-[10.5px]' : 'text-[10px]',
              rollup.total_docs === 0 && 'ml-auto',
            )}
          >
            {node.count}
          </span>
          <CompletenessIcon
            status={rollup.status}
            presentCount={rollup.total_present}
            requiredCount={rollup.total_required}
            size={isZone ? 'sm' : 'xs'}
          />
        </button>

        {open && (
          <ul
            className={cn(
              'mt-0.5 space-y-0.5 border-l border-surface-border pl-1.5',
              isZone ? 'ml-4' : 'ml-3',
            )}
          >
            {node.children.map((child) => renderNode(child))}
          </ul>
        )}
      </li>
    );
  }

  function renderNode(node: TreeNode) {
    return node.kind === 'leaf' ? renderLeaf(node) : renderContainer(node);
  }

  return (
    <nav aria-label="Binder folders" className="text-sm select-none">
      <ul className="space-y-0.5 px-2 py-1">
        {query !== '' && topNodes.length === 0 && (
          <li className="px-2 py-3 text-center text-xs text-ink-muted">
            No folders match “{filter}”.
          </li>
        )}
        {topNodes.map((node) => renderNode(node))}
      </ul>
    </nav>
  );
}
